import { useCollectionView } from '../../app/use-collection-view.js';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  DateRangePicker,
  FormErrorSummary,
  Combobox,
  IconButton,
  Input,
  Modal,
  Select,
  StatusPill,
  Textarea,
  formatCivilRange,
  todayCivilDate,
  useIsWideViewport,
  useSnackbar,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  CopyPlus,
  ListChecks,
  PackageOpen,
  PencilLine,
  Plus,
  RotateCcw,
  Trash2,
  Archive,
} from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  getApiError,
  protocolsApi,
  servicesApi,
  type Protocol,
  type ProtocolActivityFilter,
  type ProtocolInput,
  type ProtocolMaterialInput,
  type ProtocolSort,
} from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
} from '../../app/data-view.js';
import type { ProtocolsView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

export interface ProtocolCapabilities {
  canCreate: boolean;
  canUpdate: boolean;
  canArchive: boolean;
  canReadMaterials?: boolean;
  canManageMaterials?: boolean;
}

const stepSchema = z.object({
  id: z.string().optional(),
  title: z.string().trim().min(2, 'Informe o título da etapa.').max(160),
  description: z.string().trim().max(1000, 'Use até 1000 caracteres.'),
  estimatedMinutes: z
    .number()
    .int('Informe a duração em minutos inteiros.')
    .min(1, 'A duração mínima é de 1 minuto.')
    .max(1440, 'A duração máxima é de 24 horas.')
    .nullable(),
});
const protocolSchema = z.object({
  serviceId: z.string().uuid('Escolha o serviço do protocolo.'),
  displayName: z.string().trim().min(2, 'Informe o nome do protocolo.').max(160),
  description: z.string().trim().max(1000, 'Use até 1000 caracteres.'),
  steps: z.array(stepSchema).min(1, 'Informe pelo menos uma etapa.').max(50, 'Use até 50 etapas.'),
});
type ProtocolForm = z.infer<typeof protocolSchema>;

const activityOptions: Array<{ value: ProtocolActivityFilter; label: string }> = [
  { value: 'ALL', label: 'Todos' },
  { value: 'ACTIVE', label: 'Ativos' },
  { value: 'INACTIVE', label: 'Arquivados' },
];
const columns: Array<{ sort: ProtocolSort; label: string }> = [
  { sort: 'displayName', label: 'Protocolo' },
  { sort: 'serviceDisplayName', label: 'Serviço' },
  { sort: 'createdAt', label: 'Cadastrado em' },
];

function useServiceOptions() {
  return useQuery({
    queryKey: ['company', 'protocols', 'service-options'],
    queryFn: () =>
      servicesApi.list({
        activity: 'ACTIVE',
        currency: 'ALL',
        sort: 'displayName',
        direction: 'asc',
        limit: 100,
        offset: 0,
      }),
    retry: false,
  });
}

export function CompanyProtocols({
  capabilities,
  timeZone,
  view,
  onView,
}: {
  capabilities: ProtocolCapabilities;
  timeZone: string;
  view: ProtocolsView;
  onView: ViewSetter<ProtocolsView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const collectionView = useCollectionView();
  const wide = useIsWideViewport() && collectionView === 'list';
  const services = useServiceOptions();
  const [createOpen, setCreateOpen] = useState(false);
  const [cloneTarget, setCloneTarget] = useState<Protocol>();
  const [activationTarget, setActivationTarget] = useState<Protocol>();
  const [materialTarget, setMaterialTarget] = useState<Protocol>();
  const [activationError, setActivationError] = useState<string>();
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    activity: view.activity,
    service: view.service,
    ...(view.from ? { from: view.from } : {}),
    ...(view.to ? { to: view.to } : {}),
    sort: view.sort,
    direction: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'protocols', filters],
    queryFn: () => protocolsApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const detail = useQuery({
    queryKey: ['company', 'protocols', 'detail', view.protocol],
    queryFn: () => protocolsApi.get(view.protocol!),
    enabled: Boolean(view.protocol),
    retry: false,
  });

  function toggleSort(column: ProtocolSort) {
    if (view.sort === column) onView({ direction: view.direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: column === 'createdAt' ? 'desc' : 'asc' });
  }

  async function setActive(protocol: Protocol) {
    setActivationError(undefined);
    try {
      await protocolsApi.setActive(protocol.id, !protocol.active);
      await queryClient.invalidateQueries({ queryKey: ['company', 'protocols'] });
      setActivationTarget(undefined);
      snackbar.success(protocol.active ? 'Protocolo arquivado.' : 'Protocolo reativado.');
    } catch (error) {
      setActivationError(getApiError(error));
    }
  }

  const serviceOptions = [
    { value: 'ALL', label: 'Todos os serviços' },
    ...(services.data?.services ?? []).map((service) => ({
      value: service.id,
      label: service.displayName,
    })),
  ];
  const total = page.data?.total ?? 0;
  /// No celular os campos de filtro ficam fechados dentro do diálogo: a etiqueta é o único sinal do
  /// que está filtrando a lista. Serviço escolhido só vira etiqueta depois que a lista chega — sem o
  /// nome, a etiqueta seria um identificador na tela.
  const chips = [
    ...(view.activity === 'ALL'
      ? []
      : [
          {
            key: 'activity',
            label: activityOptions.find((item) => item.value === view.activity)!.label,
            onRemove: () => onView({ activity: 'ALL' }),
          },
        ]),
    ...(services.data?.services ?? [])
      .filter((service) => service.id === view.service)
      .map((service) => ({
        key: 'service',
        label: service.displayName,
        onRemove: () => onView({ service: 'ALL' }),
      })),
    ...((view.from ?? view.to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: view.from, to: view.to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];

  return (
    <section aria-labelledby="company-protocols-title">
      <PageHeader
        trail="Operação / Protocolos"
        id="company-protocols-title"
        title="Protocolos"
        description="O processo de cada serviço, etapa a etapa, na ordem em que acontece."
        actions={
          <>
            {capabilities.canCreate ? (
              <CreateButton onClick={() => setCreateOpen(true)}>Cadastrar protocolo</CreateButton>
            ) : null}
          </>
        }
      />

      <FilterBar
        searchId="protocol-search"
        searchLabel="Buscar protocolo"
        searchPlaceholder="Nome, descrição ou serviço"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ activity: 'ALL', service: 'ALL', from: null, to: null })}
        count={`${total} ${total === 1 ? 'protocolo' : 'protocolos'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'protocolo' : 'protocolos'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <FilterGroup
          label="Situação"
          description="Filtrar protocolos por situação"
          options={activityOptions}
          value={view.activity}
          onChange={(activity) => onView({ activity })}
        />
        <Combobox
          label="Serviço"
          placeholder="Buscar serviço"
          selectedLabel={
            serviceOptions.find((option) => option.value === view.service)?.label ?? ''
          }
          options={serviceOptions}
          loading={services.isLoading}
          onSelect={(choice) => {
            if (choice.kind === 'option') onView({ service: choice.value });
          }}
          onClear={() => onView({ service: 'ALL' })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Cadastro</span>
          <DateRangePicker
            label="Dias de cadastro"
            value={{ from: view.from, to: view.to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={todayCivilDate(timeZone)}
          />
        </div>
      </FilterBar>

      {services.error ? <ErrorCard message={getApiError(services.error)} /> : null}

      {page.isLoading ? (
        <LoadingCard label="Carregando protocolos" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={ListChecks}
          title="Nenhum protocolo encontrado"
          description="Ajuste os filtros ou cadastre o primeiro protocolo da empresa."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          {wide ? null : (
            <div>
              <SortBar
                label="Ordenação dos protocolos"
                columns={columns}
                sort={view.sort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.protocols.map((protocol) => (
                  <li key={protocol.id}>
                    <ProtocolCard
                      protocol={protocol}
                      capabilities={capabilities}
                      onEdit={() => onView({ protocol: protocol.id })}
                      onClone={() => setCloneTarget(protocol)}
                      onActivation={() => setActivationTarget(protocol)}
                      onMaterials={() => setMaterialTarget(protocol)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {wide ? (
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Protocolos da empresa</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  {columns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.sort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th className="w-28 px-5 py-3 text-xs font-semibold text-content-muted">
                    Etapas
                  </th>
                  <th className="w-40">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.protocols.map((protocol) => (
                  <tr key={protocol.id}>
                    <td className="px-5 py-4">
                      <strong className="block truncate text-sm">{protocol.displayName}</strong>
                      <span className="hue-list-description mt-1 block truncate text-xs text-content-muted">
                        {protocol.description ?? 'Sem descrição'}
                      </span>
                      {protocol.active ? null : <StatusPill tone="neutral">Arquivado</StatusPill>}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {protocol.serviceDisplayName}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatDate(protocol.createdAt, timeZone)}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-xs tabular-nums text-content-muted">
                      {describeSteps(protocol)}
                    </td>
                    <td className="px-3 py-4">
                      <Actions
                        protocol={protocol}
                        capabilities={capabilities}
                        onEdit={() => onView({ protocol: protocol.id })}
                        onClone={() => setCloneTarget(protocol)}
                        onActivation={() => setActivationTarget(protocol)}
                        onMaterials={() => setMaterialTarget(protocol)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'protocolo' : 'protocolos')}
          />
        </div>
      )}

      <ProtocolDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        protocol={null}
        capabilities={capabilities}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'protocols'] });
          setCreateOpen(false);
          snackbar.success('Protocolo cadastrado.');
        }}
      />
      <ProtocolDialog
        open={Boolean(cloneTarget)}
        onOpenChange={(open) => {
          if (!open) setCloneTarget(undefined);
        }}
        protocol={null}
        template={cloneTarget ?? null}
        capabilities={capabilities}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'protocols'] });
          setCloneTarget(undefined);
          snackbar.success('Cópia do protocolo cadastrada.');
        }}
      />
      <ProtocolDialog
        open={Boolean(view.protocol)}
        onOpenChange={(open) => {
          if (!open) onView({ protocol: undefined });
        }}
        protocol={detail.data ?? null}
        loading={detail.isLoading}
        {...(detail.error ? { error: getApiError(detail.error) } : {})}
        capabilities={capabilities}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'protocols'] });
          onView({ protocol: undefined });
          snackbar.success('Protocolo atualizado.');
        }}
      />
      {activationTarget ? (
        <Modal
          open
          onOpenChange={() => setActivationTarget(undefined)}
          title={activationTarget.active ? 'Arquivar protocolo' : 'Reativar protocolo'}
          description={
            activationTarget.active
              ? 'O protocolo deixa de aparecer na operação, mas o cadastro e o histórico permanecem.'
              : 'O protocolo volta a ficar disponível para a operação.'
          }
          footer={
            <>
              <Button variant="outline" onClick={() => setActivationTarget(undefined)}>
                Cancelar
              </Button>
              <Button onClick={() => void setActive(activationTarget)}>
                {activationTarget.active ? 'Arquivar protocolo' : 'Reativar protocolo'}
              </Button>
            </>
          }
        >
          <p className="text-sm">
            {activationTarget.active ? 'Arquivar' : 'Reativar'}{' '}
            <strong>{activationTarget.displayName}</strong>?
          </p>
          {activationError ? <RequestAlert message={activationError} /> : null}
        </Modal>
      ) : null}
      {materialTarget ? (
        <ProtocolMaterialsDialog
          protocol={materialTarget}
          canManage={Boolean(capabilities.canManageMaterials)}
          onOpenChange={(open) => {
            if (!open) setMaterialTarget(undefined);
          }}
        />
      ) : null}
    </section>
  );
}

function ProtocolCard({
  protocol,
  capabilities,
  onEdit,
  onClone,
  onActivation,
  onMaterials,
}: {
  protocol: Protocol;
  capabilities: ProtocolCapabilities;
  onEdit: () => void;
  onClone: () => void;
  onActivation: () => void;
  onMaterials: () => void;
}) {
  return (
    <div className="p-4">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
          <ListChecks className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <strong className="block truncate text-sm">{protocol.displayName}</strong>
          <span className="mt-1 block truncate text-xs text-content-muted">
            {protocol.serviceDisplayName}
          </span>
          <p className="mt-2 text-xs text-content-muted">{describeSteps(protocol)}</p>
          {protocol.active ? null : (
            <div className="mt-2">
              <StatusPill tone="neutral">Arquivado</StatusPill>
            </div>
          )}
        </div>
        <Actions
          protocol={protocol}
          capabilities={capabilities}
          onEdit={onEdit}
          onClone={onClone}
          onActivation={onActivation}
          onMaterials={onMaterials}
        />
      </div>
    </div>
  );
}

function Actions({
  protocol,
  capabilities,
  onEdit,
  onClone,
  onActivation,
  onMaterials,
}: {
  protocol: Protocol;
  capabilities: ProtocolCapabilities;
  onEdit: () => void;
  onClone: () => void;
  onActivation: () => void;
  onMaterials: () => void;
}) {
  const canActivate = protocol.active ? capabilities.canArchive : capabilities.canUpdate;
  return (
    <div className="flex min-w-max justify-end gap-1">
      {capabilities.canUpdate ? (
        <IconButton
          label="Editar protocolo"
          icon={<PencilLine className="size-4" />}
          onClick={onEdit}
        />
      ) : null}
      {capabilities.canCreate ? (
        <IconButton
          label="Duplicar protocolo"
          icon={<CopyPlus className="size-4" />}
          onClick={onClone}
        />
      ) : null}
      {capabilities.canReadMaterials ? (
        <IconButton
          label="Materiais previstos"
          icon={<PackageOpen className="size-4" />}
          onClick={onMaterials}
        />
      ) : null}
      {canActivate ? (
        <IconButton
          label={protocol.active ? 'Arquivar' : 'Reativar'}
          {...(protocol.active ? { tone: 'danger' as const } : {})}
          icon={protocol.active ? <Archive className="size-4" /> : <RotateCcw className="size-4" />}
          onClick={onActivation}
        />
      ) : null}
    </div>
  );
}

const unitLabels = { UNIT: 'un', MILLILITER: 'ml', GRAM: 'g' } as const;

function ProtocolMaterialsDialog({
  protocol,
  canManage,
  onOpenChange,
}: {
  protocol: Protocol;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const snackbar = useSnackbar();
  const [rows, setRows] = useState<ProtocolMaterialInput[]>([]);
  const [requestError, setRequestError] = useState<string>();
  const materials = useQuery({
    queryKey: ['company', 'protocols', protocol.id, 'materials'],
    queryFn: () => protocolsApi.getMaterials(protocol.id),
    retry: false,
  });
  const products = useQuery({
    queryKey: ['company', 'protocols', 'material-products'],
    queryFn: () => protocolsApi.listMaterialProducts(),
    retry: false,
  });
  useEffect(() => {
    // A resposta remota é o estado inicial editável do diálogo; mudanças seguintes são locais.
    if (materials.data)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRows(
        materials.data.map(({ protocolStepId, productId, quantity }) => ({
          protocolStepId,
          productId,
          quantity,
        })),
      );
  }, [materials.data]);
  async function save() {
    setRequestError(undefined);
    try {
      await protocolsApi.replaceMaterials(protocol.id, rows);
      snackbar.success('Materiais previstos atualizados.');
      onOpenChange(false);
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }
  const setProduct = (index: number, productId: string) =>
    setRows((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? { ...item, productId } : item)),
    );
  const productOptions = (products.data ?? []).map((product) => ({
    value: product.id,
    label: `${product.displayName} (${unitLabels[product.baseUnit]})`,
  }));
  const stepOptions = protocol.steps.map((step) => ({
    value: step.id,
    label: `${step.position}. ${step.title}`,
  }));
  /// Só existe linha nova quando há produto para escolher, e a primeira opção é o padrão dela.
  const firstProductId = products.data?.[0]?.id;
  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title="Materiais previstos"
      description={`Informe o consumo esperado em cada etapa de ${protocol.displayName}. As quantidades usam a unidade-base do produto.`}
      footer={
        canManage ? (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={() => void save()}>Salvar materiais</Button>
          </>
        ) : undefined
      }
    >
      {materials.isLoading || products.isLoading ? (
        <LoadingCard label="Carregando materiais" />
      ) : materials.isError || products.isError ? (
        <ErrorCard message="Não foi possível carregar os materiais." />
      ) : (
        <div className="grid gap-3">
          {rows.map((row, index) => (
            <div
              key={`${index}-${row.protocolStepId}-${row.productId}`}
              className="grid gap-3 rounded-2xl border border-surface-border p-4 sm:grid-cols-[1fr_1fr_8rem_auto]"
            >
              <Select
                label="Etapa"
                value={row.protocolStepId}
                options={stepOptions}
                disabled={!canManage}
                onValueChange={(value) =>
                  setRows((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, protocolStepId: value } : item,
                    ),
                  )
                }
              />
              <Combobox
                label="Produto"
                placeholder="Buscar produto"
                selectedLabel={
                  productOptions.find((option) => option.value === row.productId)?.label ?? ''
                }
                options={productOptions}
                emptyLabel="Nenhum produto com esse nome"
                disabled={!canManage}
                onClear={() => setProduct(index, '')}
                onSelect={(choice) => {
                  if (choice.kind === 'option') setProduct(index, choice.value);
                }}
              />
              <Input
                label="Quantidade"
                value={row.quantity}
                disabled={!canManage}
                inputMode="decimal"
                onChange={(event) =>
                  setRows((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, quantity: event.target.value } : item,
                    ),
                  )
                }
              />
              {canManage ? (
                <div className="flex items-end">
                  <IconButton
                    label={`Remover material ${index + 1}`}
                    tone="danger"
                    icon={<Trash2 className="size-4" />}
                    onClick={() =>
                      setRows((current) => current.filter((_, itemIndex) => itemIndex !== index))
                    }
                  />
                </div>
              ) : null}
            </div>
          ))}
          {rows.length === 0 ? (
            <EmptyCard
              icon={PackageOpen}
              title="Nenhum material previsto"
              description="Adicione os produtos usados em cada etapa."
            />
          ) : null}
          {canManage ? (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setRows((current) => [
                  ...current,
                  {
                    protocolStepId: protocol.steps[0]!.id,
                    productId: firstProductId!,
                    quantity: '',
                  },
                ])
              }
              disabled={!firstProductId}
            >
              <Plus className="size-4" />
              Adicionar material
            </Button>
          ) : null}
          {requestError ? <RequestAlert message={requestError} /> : null}
        </div>
      )}
    </Modal>
  );
}

function ProtocolDialog({
  open,
  onOpenChange,
  protocol,
  template = null,
  loading = false,
  error,
  capabilities,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  protocol: Protocol | null;
  template?: Protocol | null;
  loading?: boolean;
  error?: string;
  capabilities: ProtocolCapabilities;
  onSaved: () => Promise<void>;
}) {
  const formId = useId();
  const [requestError, setRequestError] = useState<string>();
  const services = useServiceOptions();
  const form = useForm<ProtocolForm>({
    resolver: zodResolver(protocolSchema),
    defaultValues: emptyProtocol(),
  });
  const steps = useFieldArray({ control: form.control, name: 'steps' });
  /// `useWatch` promete um formulário pela metade, mas este nunca está: `defaultValues` preenche
  /// todo campo no primeiro render e nenhum controle devolve `undefined`.
  const values = useWatch({ control: form.control }) as ProtocolForm;
  useEffect(() => {
    if (open)
      form.reset(protocol ? formOf(protocol) : template ? cloneFormOf(template) : emptyProtocol());
  }, [form, open, protocol, template]);

  function changeOpen(next: boolean) {
    if (!next) setRequestError(undefined);
    onOpenChange(next);
  }

  async function save(data: ProtocolForm) {
    setRequestError(undefined);
    const payload: ProtocolInput = {
      serviceId: data.serviceId,
      displayName: data.displayName,
      description: data.description || null,
      steps: data.steps.map((step) => ({
        ...(step.id ? { id: step.id } : {}),
        title: step.title,
        description: step.description || null,
        estimatedMinutes: step.estimatedMinutes,
      })),
    };
    try {
      if (protocol) await protocolsApi.update(protocol.id, payload);
      else await protocolsApi.create(payload);
      await onSaved();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  const disabled = Boolean(protocol) && !capabilities.canUpdate;
  const serviceOptions = (services.data?.services ?? []).map((service) => ({
    value: service.id,
    label: service.displayName,
  }));
  return (
    <Modal
      open={open}
      onOpenChange={changeOpen}
      title={protocol ? 'Editar protocolo' : 'Cadastrar protocolo'}
      description="As etapas seguem a ordem desta lista; cada aplicação futura guardará o processo como ele estava."
      footer={
        !loading &&
        !error &&
        ((!protocol && capabilities.canCreate) || (protocol && capabilities.canUpdate)) ? (
          <>
            <FormErrorSummary form={formId} className="basis-full sm:basis-auto sm:flex-1" />
            <Button type="button" variant="outline" onClick={() => changeOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" form={formId} loading={form.formState.isSubmitting}>
              {protocol ? 'Salvar protocolo' : 'Cadastrar protocolo'}
            </Button>
          </>
        ) : undefined
      }
    >
      {loading ? (
        <LoadingCard label="Carregando protocolo" />
      ) : error ? (
        <ErrorCard message={error} />
      ) : (
        <form id={formId} noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Select
                label="Serviço"
                value={values.serviceId}
                options={serviceOptions}
                onValueChange={(value) =>
                  form.setValue('serviceId', value, { shouldValidate: true })
                }
                disabled={disabled}
              />
              {form.formState.errors.serviceId ? (
                <p role="alert" className="text-xs text-danger">
                  {form.formState.errors.serviceId.message}
                </p>
              ) : null}
            </div>
            <Input
              label="Nome do protocolo"
              error={form.formState.errors.displayName?.message}
              disabled={disabled}
              {...form.register('displayName')}
            />
            <Textarea
              label="Descrição"
              rows={3}
              error={form.formState.errors.description?.message}
              disabled={disabled}
              {...form.register('description')}
            />
          </div>

          <div className="mt-6 border-t border-surface-border pt-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold">Etapas do processo</h2>
              {disabled ? null : (
                <Button type="button" variant="outline" onClick={() => steps.append(emptyStep())}>
                  <Plus className="size-4" />
                  Adicionar etapa
                </Button>
              )}
            </div>
            {/* O erro da lista inteira mora em `root`: o resolver reserva o nível do campo para o
                erro de cada etapa. */}
            {form.formState.errors.steps?.root?.message ? (
              <RequestAlert message={form.formState.errors.steps.root.message} />
            ) : null}
            <ol className="grid gap-3">
              {steps.fields.map((field, index) => (
                <li
                  key={field.id}
                  className="rounded-2xl border border-surface-border bg-surface-sunken p-4"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold text-content-muted">
                      Etapa {index + 1}
                    </span>
                    {disabled ? null : (
                      <div className="flex gap-1">
                        <IconButton
                          label={`Mover etapa ${index + 1} para cima`}
                          icon={<ArrowUp className="size-4" />}
                          disabled={index === 0}
                          onClick={() => steps.move(index, index - 1)}
                        />
                        <IconButton
                          label={`Mover etapa ${index + 1} para baixo`}
                          icon={<ArrowDown className="size-4" />}
                          disabled={index === steps.fields.length - 1}
                          onClick={() => steps.move(index, index + 1)}
                        />
                        <IconButton
                          label={`Remover etapa ${index + 1}`}
                          tone="danger"
                          icon={<Trash2 className="size-4" />}
                          onClick={() => steps.remove(index)}
                        />
                      </div>
                    )}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
                    <Input
                      label="Título"
                      error={form.formState.errors.steps?.[index]?.title?.message}
                      disabled={disabled}
                      {...form.register(`steps.${index}.title`)}
                    />
                    <Input
                      label="Duração em minutos"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={1440}
                      error={form.formState.errors.steps?.[index]?.estimatedMinutes?.message}
                      disabled={disabled}
                      hint="Opcional"
                      /// A conversão recebe `''` do campo em branco e `null` quando o formulário
                      /// devolve a etapa sem estimativa. `Number` transformaria os dois em zero, e a
                      /// duração opcional viraria uma recusa.
                      {...form.register(`steps.${index}.estimatedMinutes`, {
                        setValueAs: (value: unknown) =>
                          value === '' || value === null || value === undefined
                            ? null
                            : Number(value),
                      })}
                    />
                    <div className="sm:col-span-2">
                      <Textarea
                        label="Como fazer"
                        rows={2}
                        error={form.formState.errors.steps?.[index]?.description?.message}
                        disabled={disabled}
                        {...form.register(`steps.${index}.description`)}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {requestError ? <RequestAlert message={requestError} /> : null}
        </form>
      )}
    </Modal>
  );
}

function emptyStep(): ProtocolForm['steps'][number] {
  return { title: '', description: '', estimatedMinutes: null };
}

function emptyProtocol(): ProtocolForm {
  return { serviceId: '', displayName: '', description: '', steps: [emptyStep()] };
}

function formOf(protocol: Protocol): ProtocolForm {
  return {
    serviceId: protocol.serviceId,
    displayName: protocol.displayName,
    description: protocol.description ?? '',
    steps: protocol.steps.map((step) => ({
      id: step.id,
      title: step.title,
      description: step.description ?? '',
      estimatedMinutes: step.estimatedMinutes,
    })),
  };
}

function cloneFormOf(protocol: Protocol): ProtocolForm {
  const form = formOf(protocol);
  return {
    ...form,
    displayName: `Cópia de ${protocol.displayName}`.slice(0, 160),
    steps: form.steps.map((step) => ({ ...step, id: undefined })),
  };
}

/// A soma só aparece quando toda etapa foi estimada: somar metade das etapas anunciaria um total
/// menor do que o processo leva.
function describeSteps(protocol: Protocol): string {
  const count = `${protocol.steps.length} ${protocol.steps.length === 1 ? 'etapa' : 'etapas'}`;
  if (protocol.steps.some((step) => step.estimatedMinutes === null)) return count;
  const minutes = protocol.steps.reduce((total, step) => total + (step.estimatedMinutes ?? 0), 0);
  return `${count} · ${formatDuration(minutes)}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}min` : `${hours}h`;
}
